'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Upload, CheckCircle2, AlertTriangle, XCircle, Info } from 'lucide-react';
import Image from 'next/image';
import { DPI_MAX_BYTES, DPI_MAX_PIXELS, DPI_MULTIPART_BYTES } from '@/lib/ai/dpi-limits';

type Copy = {
  dropTitle: string;
  dropHint: string;
  chooseFile: string;
  yourPhoto: string;
  pixels: string;
  format: string;
  dpi: string;
  quality: string;
  excellent: string;
  good: string;
  fair: string;
  poor: string;
  intro: string;
  tipTitle: string;
  tip: string;
  privacy: string;
  invalidFile: string;
};

const COPY: Record<string, Copy> = {
  nl: {
    dropTitle: 'Sleep je foto hierheen of klik om te kiezen',
    dropHint: 'JPG, PNG of WebP - je foto verlaat je computer niet',
    chooseFile: 'Kies een foto',
    yourPhoto: 'Jouw foto',
    pixels: 'pixels',
    format: 'Printformaat',
    dpi: 'DPI',
    quality: 'Kwaliteit',
    excellent: 'Uitstekend',
    good: 'Goed',
    fair: 'Acceptabel',
    poor: 'Te laag',
    intro: 'Upload een foto en zie direct op welke formaten hij scherp print. Alles gebeurt in je browser; we uploaden niets.',
    tipTitle: 'Hoe lees je dit?',
    tip: 'Vanaf 300 DPI is een print gegarandeerd scherp, 150-300 DPI is prima voor canvas en posters op kijkafstand, onder 100 DPI wordt een foto zichtbaar onscherp. Twijfel je? In onze editor zie je tijdens het ontwerpen dezelfde DPI-waarschuwing.',
    privacy: 'Je foto wordt alleen in je browser geanalyseerd en nergens geupload.',
    invalidFile: 'Kies een JPG-, PNG- of WebP-afbeelding.',
  },
  de: {
    dropTitle: 'Foto hierher ziehen oder klicken zum Auswählen',
    dropHint: 'JPG, PNG oder WebP - Ihr Foto verlässt Ihren Computer nicht',
    chooseFile: 'Foto auswaehlen',
    yourPhoto: 'Ihr Foto',
    pixels: 'Pixel',
    format: 'Druckformat',
    dpi: 'DPI',
    quality: 'Qualität',
    excellent: 'Ausgezeichnet',
    good: 'Gut',
    fair: 'Akzeptabel',
    poor: 'Zu niedrig',
    intro: 'Laden Sie ein Foto hoch und sehen Sie sofort, in welchen Formaten es scharf gedruckt wird. Alles passiert im Browser; nichts wird hochgeladen.',
    tipTitle: 'Wie liest man das?',
    tip: 'Ab 300 DPI ist ein Druck garantiert scharf, 150-300 DPI ist für Leinwand und Poster auf Betrachtungsabstand völlig ausreichend, unter 100 DPI wird ein Foto sichtbar unscharf.',
    privacy: 'Ihr Foto wird nur im Browser analysiert und nirgendwo hochgeladen.',
    invalidFile: 'Bitte wählen Sie ein JPG-, PNG- oder WebP-Bild.',
  },
  fr: {
    dropTitle: 'Glissez votre photo ici ou cliquez pour choisir',
    dropHint: 'JPG, PNG ou WebP - votre photo ne quitte pas votre ordinateur',
    chooseFile: 'Choisir une photo',
    yourPhoto: 'Votre photo',
    pixels: 'pixels',
    format: 'Format d’impression',
    dpi: 'DPI',
    quality: 'Qualité',
    excellent: 'Excellente',
    good: 'Bonne',
    fair: 'Acceptable',
    poor: 'Trop faible',
    intro: 'Téléchargez une photo et voyez immédiatement dans quels formats elle s’imprime nettement. Tout se passe dans votre navigateur.',
    tipTitle: 'Comment lire ce tableau ?',
    tip: 'À partir de 300 DPI, l’impression est garantie nette ; 150-300 DPI convient très bien aux toiles et posters vus à distance ; en dessous de 100 DPI, la photo devient visiblement floue.',
    privacy: 'Votre photo est analysée uniquement dans votre navigateur.',
    invalidFile: 'Choisissez une image JPG, PNG ou WebP.',
  },
  en: {
    dropTitle: 'Drop your photo here or click to choose',
    dropHint: 'JPG, PNG or WebP - your photo never leaves your computer',
    chooseFile: 'Choose a photo',
    yourPhoto: 'Your photo',
    pixels: 'pixels',
    format: 'Print size',
    dpi: 'DPI',
    quality: 'Quality',
    excellent: 'Excellent',
    good: 'Good',
    fair: 'Acceptable',
    poor: 'Too low',
    intro: 'Upload a photo and instantly see which sizes it will print sharply at. Everything happens in your browser; nothing is uploaded.',
    tipTitle: 'How to read this',
    tip: 'From 300 DPI a print is guaranteed sharp, 150-300 DPI is fine for canvas and posters at viewing distance, below 100 DPI a photo becomes visibly soft.',
    privacy: 'Your photo is analysed in your browser only and never uploaded.',
    invalidFile: 'Please choose a JPG, PNG or WebP image.',
  },
};

// Populaire printformaten in cm (breedte x hoogte)
const FORMATS: Array<{ label: string; w: number; h: number }> = [
  { label: '20 × 30 cm', w: 20, h: 30 },
  { label: '30 × 20 cm', w: 30, h: 20 },
  { label: '30 × 40 cm', w: 30, h: 40 },
  { label: '40 × 60 cm', w: 40, h: 60 },
  { label: '50 × 70 cm', w: 50, h: 70 },
  { label: '60 × 80 cm', w: 60, h: 80 },
  { label: '60 × 90 cm', w: 60, h: 90 },
  { label: '80 × 120 cm', w: 80, h: 120 },
  { label: '100 × 70 cm', w: 100, h: 70 },
  { label: '120 × 80 cm', w: 120, h: 80 },
  { label: 'A3 (29,7 × 42 cm)', w: 29.7, h: 42 },
  { label: 'A2 (42 × 59,4 cm)', w: 42, h: 59.4 },
  { label: 'A1 (59,4 × 84,1 cm)', w: 59.4, h: 84.1 },
  { label: 'A0 (84,1 × 118,9 cm)', w: 84.1, h: 118.9 },
];

function dpiFor(pixelsW: number, pixelsH: number, cmW: number, cmH: number) {
  // DPI wordt bepaald door de krapste zijde; orientatie-onafhankelijk rekenen.
  const long = Math.max(pixelsW, pixelsH);
  const short = Math.min(pixelsW, pixelsH);
  const longCm = Math.max(cmW, cmH);
  const shortCm = Math.min(cmW, cmH);
  const dpiLong = long / (longCm / 2.54);
  const dpiShort = short / (shortCm / 2.54);
  return Math.floor(Math.min(dpiLong, dpiShort));
}

const TARGET_COPY: Record<string, { before: string; result: string }> = {
  nl: { before: 'We checken je foto voor je gekozen formaat: {size}.', result: 'Jouw gekozen formaat' },
  en: { before: 'We will check your photo for your chosen size: {size}.', result: 'Your chosen size' },
  de: { before: 'Wir prüfen Ihr Foto für Ihr gewähltes Format: {size}.', result: 'Ihr gewähltes Format' },
  fr: { before: 'Nous vérifions votre photo pour le format choisi : {size}.', result: 'Votre format choisi' },
};

function targetLabel(target: { widthMm: number; heightMm: number }, locale: string) {
  const cm = (mm: number) => new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(mm / 10);
  return `${cm(target.widthMm)} × ${cm(target.heightMm)} cm`;
}

export function DpiChecker({ locale, target = null }: { locale: string; target?: { widthMm: number; heightMm: number } | null }) {
  const copy = COPY[locale.slice(0, 2)] || COPY.nl;
  const targetCopy = TARGET_COPY[locale.slice(0, 2)] || TARGET_COPY.en;
  const actions = ({
    nl: { improve: 'Resolutie verbeteren met AI', busy: 'Afbeelding verbeteren…', download: 'Verbeterde afbeelding downloaden', before: 'Originele DPI', after: 'Nieuwe DPI', consent: 'Voor AI-verbetering wordt je foto verstuurd naar onze beeldverwerkingsdienst. AI kan details veranderen; controleer het resultaat. Maximaal 20 MB en 16 megapixels, 3 aanvragen per dag per netwerk.', failed: 'Verbeteren is niet gelukt of de gebruikslimiet is bereikt. Je originele foto blijft beschikbaar.', notice: 'Meer pixels betekent niet automatisch meer echte details. DPI is een indicatie bij het gekozen printformaat, geen scherptegarantie.', privacy: 'De DPI-check gebeurt lokaal. Alleen na klikken op AI-verbetering wordt je foto verstuurd.', intro: 'Kies een foto om lokaal de DPI per printformaat te controleren. Je kunt daarna optioneel de resolutie met AI verbeteren.', limit: 'Deze afbeelding is te groot voor deze AI-tool (maximaal 20 MB en 16 megapixels).' },
    en: { improve: 'Improve resolution with AI', busy: 'Improving image…', download: 'Download improved image', before: 'Original DPI', after: 'New DPI', consent: 'AI enhancement sends your photo to our image-processing service. AI may change details; review the result. Maximum 20 MB and 16 megapixels, 3 requests per day per network.', failed: 'Enhancement failed or the usage limit was reached. Your original photo is still available.', notice: 'More pixels do not automatically mean more real detail. DPI indicates resolution at the chosen print size, not guaranteed sharpness.', privacy: 'The DPI check stays local. Your photo is only sent after clicking AI enhancement.', intro: 'Choose a photo to check DPI locally for each print size. You can then optionally improve its resolution with AI.', limit: 'This image exceeds the AI limit of 20 MB or 16 megapixels.' },
    de: { improve: 'Auflösung mit KI verbessern', busy: 'Bild wird verbessert…', download: 'Verbessertes Bild herunterladen', before: 'Original-DPI', after: 'Neue DPI', consent: 'Für die KI-Verbesserung wird Ihr Foto an unseren Bildverarbeitungsdienst gesendet. KI kann Details verändern; prüfen Sie das Ergebnis. Maximal 20 MB und 16 Megapixel, 3 Anfragen pro Tag und Netzwerk.', failed: 'Verbesserung fehlgeschlagen oder Nutzungslimit erreicht. Ihr Original bleibt verfügbar.', notice: 'Mehr Pixel bedeuten nicht automatisch mehr echte Details. DPI ist ein Richtwert für das Druckformat, keine Schärfegarantie.', privacy: 'Die DPI-Prüfung bleibt lokal. Erst nach Klick auf KI-Verbesserung wird Ihr Foto gesendet.', intro: 'Wählen Sie ein Foto für die lokale DPI-Prüfung je Druckformat. Danach können Sie die Auflösung optional mit KI verbessern.', limit: 'Dieses Bild überschreitet das KI-Limit von 20 MB oder 16 Megapixeln.' },
    fr: { improve: 'Améliorer la résolution avec l’IA', busy: 'Amélioration en cours…', download: 'Télécharger l’image améliorée', before: 'DPI d’origine', after: 'Nouveaux DPI', consent: 'L’amélioration IA envoie votre photo à notre service de traitement d’images. L’IA peut modifier les détails : vérifiez le résultat. Maximum 20 Mo et 16 mégapixels, 3 demandes par jour et par réseau.', failed: 'Échec de l’amélioration ou limite atteinte. Votre original reste disponible.', notice: 'Plus de pixels ne signifie pas automatiquement plus de détails réels. Les DPI indiquent la résolution au format choisi, sans garantie de netteté.', privacy: 'La vérification DPI reste locale. La photo est envoyée uniquement après un clic sur l’amélioration IA.', intro: 'Choisissez une photo pour vérifier localement les DPI par format. Vous pouvez ensuite améliorer sa résolution avec l’IA.', limit: 'Cette image dépasse la limite IA de 20 Mo ou 16 mégapixels.' },
  } as const)[locale.slice(0, 2) as 'nl' | 'en' | 'de' | 'fr'] || null;
  const actionCopy = actions || { improve: 'Improve resolution with AI', busy: 'Improving…', download: 'Download', before: 'Original DPI', after: 'New DPI', consent: 'Your photo will be sent for AI processing. Maximum 20 MB and 16 megapixels.', failed: 'Enhancement unavailable.', notice: 'DPI is not a sharpness guarantee.', privacy: 'Local check; AI processing sends your photo.', intro: 'Choose a photo to check its DPI.', limit: 'Image too large.' };
  const [image, setImage] = useState<{ name: string; width: number; height: number; preview: string } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [enhanced, setEnhanced] = useState<{ width: number; height: number; preview: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const version = useRef(0);
  const inFlight = useRef(false);
  useEffect(() => () => { if (image) URL.revokeObjectURL(image.preview); }, [image]);
  useEffect(() => () => { if (enhanced) URL.revokeObjectURL(enhanced.preview); }, [enhanced]);
  useEffect(() => () => { version.current++; }, []);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = useCallback((file: File | undefined) => {
    const selectedVersion = ++version.current;
    setFile(null);
    setEnhanced(null);
    setError(null);
    if (!file || !/^image\/(jpeg|png|webp)$/i.test(file.type)) {
      setError(copy.invalidFile);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      if (selectedVersion !== version.current) { URL.revokeObjectURL(url); return; }
      setFile(file);
      setImage({ name: file.name, width: img.naturalWidth, height: img.naturalHeight, preview: url });
    };
    img.onerror = () => { URL.revokeObjectURL(url); if (selectedVersion === version.current) setError(copy.invalidFile); };
    img.src = url;
  }, [copy.invalidFile]);

  const improve = async () => {
    if (!file || !image || inFlight.current) return;
    if (file.size > DPI_MAX_BYTES || image.width * image.height > DPI_MAX_PIXELS) { setError(actionCopy.limit); return; }
    const selectedVersion = version.current;
    inFlight.current = true; setBusy(true); setError(null);
    let resultUrl: string | null = null;
    try {
      let body: BodyInit;
      let headers: HeadersInit | undefined;
      if (file.size > DPI_MULTIPART_BYTES) {
        const { uploadFileToStorage } = await import('@/lib/uploads/client-upload');
        const upload = await uploadFileToStorage(file, { requireProof: true, failureMessage: actionCopy.failed });
        if (selectedVersion !== version.current) return;
        body = JSON.stringify({ imageUrl: upload.url, proof: upload.proof });
        headers = { 'Content-Type': 'application/json' };
      } else {
        const form = new FormData(); form.set('file', file); body = form;
      }
      const response = await fetch('/api/tools/dpi-upscale', { method: 'POST', body, headers, signal: AbortSignal.timeout(210000) });
      if (!response.ok) throw new Error('enhancement');
      const data = await response.json();
      const result = await fetch(data.imageUrl, { signal: AbortSignal.timeout(90000) });
      if (!result.ok) throw new Error('download');
      resultUrl = URL.createObjectURL(await result.blob());
      const updated = new window.Image(); updated.src = resultUrl; await updated.decode();
      if (selectedVersion !== version.current) { URL.revokeObjectURL(resultUrl); return; }
      setEnhanced({ width: updated.naturalWidth, height: updated.naturalHeight, preview: resultUrl });
      resultUrl = null;
    } catch { if (resultUrl) URL.revokeObjectURL(resultUrl); if (selectedVersion === version.current) setError(actionCopy.failed); }
    finally { inFlight.current = false; setBusy(false); }
  };

  const rows = useMemo(() => {
    if (!image) return [];
    return FORMATS.map((format) => {
      const dpi = dpiFor(image.width, image.height, format.w, format.h);
      const currentDpi = enhanced ? dpiFor(enhanced.width, enhanced.height, format.w, format.h) : dpi;
      const level = currentDpi >= 300 ? 'excellent' : currentDpi >= 150 ? 'good' : currentDpi >= 100 ? 'fair' : 'poor';
      return { ...format, dpi, level };
    });
  }, [image, enhanced]);

  const targetRow = useMemo(() => {
    if (!image || !target) return null;
    const cmW = target.widthMm / 10;
    const cmH = target.heightMm / 10;
    const dpi = dpiFor(image.width, image.height, cmW, cmH);
    const currentDpi = enhanced ? dpiFor(enhanced.width, enhanced.height, cmW, cmH) : dpi;
    const level = currentDpi >= 300 ? 'excellent' : currentDpi >= 150 ? 'good' : currentDpi >= 100 ? 'fair' : 'poor';
    return { label: targetLabel(target, locale), dpi: currentDpi, level };
  }, [image, enhanced, target, locale]);

  const levelStyle: Record<string, { row: string; badge: string; label: keyof Copy; icon: typeof CheckCircle2 }> = {
    excellent: { row: 'bg-emerald-50', badge: 'bg-emerald-100 text-emerald-800', label: 'excellent', icon: CheckCircle2 },
    good: { row: '', badge: 'bg-green-100 text-green-800', label: 'good', icon: CheckCircle2 },
    fair: { row: '', badge: 'bg-amber-100 text-amber-800', label: 'fair', icon: AlertTriangle },
    poor: { row: 'bg-red-50/50', badge: 'bg-red-100 text-red-700', label: 'poor', icon: XCircle },
  };

  return (
    <div className="space-y-6">
      <p className="max-w-2xl text-lg leading-8 text-slate-700">{actionCopy.intro}</p>
      {target && !image && (
        <p className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-950">
          {targetCopy.before.replace('{size}', targetLabel(target, locale))}
        </p>
      )}

      <label
        onDragOver={(event) => { event.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragActive(false);
          handleFile(event.dataTransfer.files?.[0]);
        }}
        className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-10 text-center transition ${
          dragActive ? 'border-blue-500 bg-blue-50' : 'border-slate-300 bg-white hover:border-blue-400 hover:bg-slate-50'
        }`}
      >
        <Upload className="h-8 w-8 text-slate-400" />
        <span className="text-base font-semibold text-slate-900">{copy.dropTitle}</span>
        <span className="text-sm text-slate-500">JPG, PNG, WebP</span>
        <span className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm">{copy.chooseFile}</span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(event) => handleFile(event.target.files?.[0] || undefined)}
        />
      </label>

      {error && <p className="text-sm font-medium text-red-600">{error}</p>}

      {image && (
        <div className="space-y-5">
          <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <Image src={image.preview} alt="" width={64} height={64} unoptimized className="h-16 w-16 flex-none rounded-lg border border-slate-200 object-cover" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900">{copy.yourPhoto}: {image.name}</p>
              <p className="text-sm text-slate-600">{image.width.toLocaleString()} × {image.height.toLocaleString()} {copy.pixels}</p>
            </div>
          </div>

          {targetRow && (() => {
            const style = levelStyle[targetRow.level];
            const Icon = style.icon;
            return (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 border-blue-300 bg-white p-4 shadow-sm" aria-live="polite">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{targetCopy.result}</p>
                  <p className="text-lg font-black text-slate-950">{targetRow.label}: <span className="tabular-nums">{targetRow.dpi}</span> {copy.dpi}</p>
                </div>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${style.badge}`}>
                  <Icon className="h-4 w-4" />
                  {copy[style.label]}
                </span>
              </div>
            );
          })()}

          <section className="space-y-3 rounded-xl border border-blue-200 bg-white p-4" aria-busy={busy}>
            <p className="text-sm text-slate-700">{actionCopy.consent}</p>
            <button type="button" disabled={busy || !!enhanced || !file} onClick={improve} className="rounded-lg bg-blue-700 px-4 py-3 font-semibold text-white disabled:opacity-50">{busy ? actionCopy.busy : actionCopy.improve}</button>
            {enhanced && <div className="space-y-3" aria-live="polite">
              <Image src={enhanced.preview} alt="" width={enhanced.width} height={enhanced.height} unoptimized className="max-h-72 max-w-full rounded-lg object-contain" />
              <p>{enhanced.width} × {enhanced.height} {copy.pixels}</p>
              <a href={enhanced.preview} download={`${image.name.replace(/\.[^.]+$/, '')}-verbeterd.png`} className="inline-block rounded-lg bg-emerald-700 px-4 py-3 font-semibold text-white">{actionCopy.download}</a>
            </div>}
          </section>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full min-w-[420px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">{copy.format}</th>
                  <th className="px-4 py-3">{enhanced ? actionCopy.before : copy.dpi}</th>
                  {enhanced && <th className="px-4 py-3">{actionCopy.after}</th>}
                  <th className="px-4 py-3">{copy.quality}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const style = levelStyle[row.level];
                  const Icon = style.icon;
                  return (
                    <tr key={row.label} className={`border-b border-slate-100 last:border-0 ${style.row}`}>
                      <td className="px-4 py-2.5 font-medium text-slate-900">{row.label}</td>
                      <td className="px-4 py-2.5 tabular-nums text-slate-700">{row.dpi}</td>
                      {enhanced && <td className="px-4 py-2.5 font-semibold tabular-nums text-blue-800">{dpiFor(enhanced.width, enhanced.height, row.w, row.h)}</td>}
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${style.badge}`}>
                          <Icon className="h-3.5 w-3.5" />
                          {copy[style.label]}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm leading-6 text-blue-950">
        <Info className="mt-0.5 h-4 w-4 flex-none text-blue-600" />
        <div>
          <p className="font-semibold">{copy.tipTitle}</p>
          <p>{actionCopy.notice}</p>
          <p className="mt-1 text-blue-800">{actionCopy.privacy}</p>
        </div>
      </div>
    </div>
  );
}
